import React from "react";
import {
    DataGridPremium,
    GridColumns,
    GridRenderCellParams,
    GridSelectionModel,
    useGridApiRef,
} from "@mui/x-data-grid-premium";
import { addCustomDataGridFilters } from "../../../../components/common/customDataGridFilters/customDataGridFilters";
import { Link } from "@mui/material";
import CclDataGridToolbar from "../../../../components/common/cclDataGridToolbar/cclDataGridToolbar";
import CclCommonLink from "../../../../components/common/cclCommonLink";
import { ParticipantRaterDetail } from "../../../../services/types/enterpriseParticipantApiTypes";
import { AP_SURVEY_PLATFORM_KEYS } from "../../../../services/types/sessionDetailTypes";

export interface RaterManagementDataGridProProps {
    programCode: string;
    ratersDetailsList: ParticipantRaterDetail[];
    handleButtonClick: (buttonId: string, ids: number[]) => void;
    isLoading: boolean;
    loadingButtonId: string;
    showToolbar: boolean;
    isUserAssessmentManager: boolean;
    isSessionRater: boolean;
    readOnly?: boolean;
    isSuperAdmin: boolean;
    hostingPlatformKey?: number;
    surveyPlatformKey?: number;
}

export const RaterManagementDataGridPro: React.FC<RaterManagementDataGridProProps> = (props) => {
    const [ids, setIds] = React.useState<number[]>([]);
    const apiRef = useGridApiRef();

    const handleButtonClick = (buttonId: string) => {
        props.handleButtonClick(buttonId, ids);
    };

    const COLUMNS = [
        {
            field: "participantName",
            headerName: "Participant",
            type: "string",
            renderCell: (params: GridRenderCellParams) => (
                <Link href={`/registration/${encodeURIComponent(params.row.participantEsiKey)}`}>
                    {params.value}
                </Link>
            ),
            flex: 0.4,
        },
        {
            field: "raterName",
            headerName: "Rater",
            renderCell: (params: GridRenderCellParams) =>
                params.row.raterImKey != null ? (
                    <CclCommonLink
                        keyVal={params.value}
                        text={params.value}
                        to={`/user?email=${encodeURIComponent(params.row.raterEmail)}&imkey=${
                            params.row.raterImKey
                        }`}
                    />
                ) : (
                    params.value
                ),
            type: "string",
            flex: 0.4,
        },
        {
            field: "raterEmail",
            headerName: "Email",
            renderCell: (params: GridRenderCellParams) => (
                <CclCommonLink
                    text={params.value}
                    keyVal={params.value}
                    to={`mailto:${params.value}`}
                    type={"email"}
                />
            ),
            type: "string",
            flex: 0.4,
        },
        {
            field: "formMasterName",
            headerName: "Assessment",
            type: "string",
            flex: 0.4,
        },
        {
            field: "raterTypeName",
            headerName: "Type",
            type: "string",
            flex: 0.4,
        },
        {
            field: "language",
            headerName: "Language",
            type: "string",
            flex: 0.4,
        },
        {
            field: "bpiastName",
            headerName: "Status",
            type: "string",
            flex: 0.3,
        },
    ];

    const handleRowSelection = (selection: GridSelectionModel) => {
        if (selection.length === 0) {
            setIds([]);
            return;
        }
        const selectedIds = selection.filter((s) => typeof s === "number") as number[];
        setIds(selectedIds);
    };

    const viewReopen =
        !props.isSessionRater && (props.isSuperAdmin || props.isUserAssessmentManager);

    let miscButtons = props.readOnly
        ? []
        : [
              {
                  id: "add",
                  caption: "Add Rater",
                  disable: false,
                  returnSelected: false,
                  restricted: false,
                  onClick: () => props.handleButtonClick("add", ids),
              },
              {
                  id: "edit",
                  caption: "Edit Rater",
                  disable: ids.length !== 1,
                  returnSelected: true,
                  restricted: false,
                  onClick: () => props.handleButtonClick("edit", ids),
              },
              {
                  id: "delete",
                  caption: "Delete Rater",
                  disable: ids.length !== 1,
                  returnSelected: true,
                  restricted: false,
                  onClick: () => props.handleButtonClick("delete", ids),
                  loadingBtn: true,
                  isLoading: props.loadingButtonId === "delete",
              },
              ...(AP_SURVEY_PLATFORM_KEYS.includes(props.surveyPlatformKey ?? 0)
                  ? [
                        {
                            id: "invitation",
                            caption: "Resend Invitation(s)",
                            disable: ids.length <= 0,
                            returnSelected: true,
                            restricted: false,
                            onClick: () => props.handleButtonClick("invitation", ids),
                            loadingBtn: true,
                            isLoading: props.loadingButtonId === "invitation",
                        },
                        {
                            id: "reminder",
                            caption: "Resend Reminder(s)",
                            disable: ids.length <= 0,
                            returnSelected: true,
                            restricted: false,
                            onClick: () => props.handleButtonClick("reminder", ids),
                            loadingBtn: true,
                            isLoading: props.loadingButtonId === "reminder",
                        },
                    ]
                  : []),
          ];

    if (viewReopen) {
        miscButtons.push({
            id: "reopenraterassessments",
            caption: "Re-Open Assessment(s)",
            disable: ids.length <= 0,
            returnSelected: true,
            restricted: false,
            onClick: () => props.handleButtonClick("reopenraterassessments", ids),
            loadingBtn: true,
            isLoading: props.loadingButtonId === "reopenraterassessments",
        });
    }

    return (
        <>
            <DataGridPremium
                columns={
                    props.isUserAssessmentManager
                        ? props.isSessionRater
                            ? addCustomDataGridFilters(COLUMNS as GridColumns<any>)
                            : addCustomDataGridFilters(
                                  COLUMNS.filter(
                                      (c) => c.field !== "participantName",
                                  ) as GridColumns<any>,
                              )
                        : props.isSessionRater
                          ? addCustomDataGridFilters(
                                COLUMNS.filter((c) => c.field !== "actions") as GridColumns<any>,
                            )
                          : addCustomDataGridFilters(
                                COLUMNS.filter(
                                    (c) => c.field !== "actions" && c.field !== "participantName",
                                ) as GridColumns<any>,
                            )
                }
                apiRef={apiRef}
                initialState={
                    props.isSessionRater
                        ? {
                              sorting: { sortModel: [{ field: "participantName", sort: "asc" }] },
                          }
                        : {
                              sorting: { sortModel: [{ field: "raterName", sort: "asc" }] },
                          }
                }
                rows={props.ratersDetailsList}
                loading={props.isLoading}
                getRowId={(row: ParticipantRaterDetail) => row.rmKey}
                checkboxSelection
                isRowSelectable={(params) => {
                    return params.row.instrumentTypeKey === 2;
                }}
                components={{ Toolbar: CclDataGridToolbar }}
                componentsProps={{
                    toolbar: {
                        showSaveState: true,
                        hideSettingsButtons: true,
                        miscButtons: miscButtons,
                        anySelected: ids?.length > 0,
                        btnClick: handleButtonClick,
                        isAssessmentMangementRole: props.isUserAssessmentManager,
                    },
                }}
                onSelectionModelChange={handleRowSelection}
                autoHeight
            />
        </>
    );
};

export default RaterManagementDataGridPro;
